"use client";

import { Heart, Lock, Minus, Plus, ShoppingCart, Zap } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useCartStore } from "@/components/providers/cart-store";
import { useWishlistStore } from "@/components/providers/wishlist-store";
import { formatPrice } from "@/lib/utils";
import type { Product, ProductVariant } from "@/types";

export function ProductActions({
  product,
  selectedVariant,
  compact = false
}: {
  product: Product;
  selectedVariant?: ProductVariant | null;
  compact?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const addItem = useCartStore((s) => s.addItem);
  const toggleWishlist = useWishlistStore((s) => s.toggle);
  const isWishlisted = useWishlistStore((s) => s.has(product.id));

  useEffect(() => setMounted(true), []);

  const safeIsWishlisted = mounted ? isWishlisted : false;

  const currentPrice = selectedVariant ? selectedVariant.price : product.price;
  const currentStock = selectedVariant?.stock !== undefined ? selectedVariant.stock : product.stock;
  const currentMrp = selectedVariant?.mrp ?? product.mrp;
  const currentDiscount = currentMrp > currentPrice ? Math.round(((currentMrp - currentPrice) / currentMrp) * 100) : product.discount;

  const maxQuantity = Math.max(1, Math.min(currentStock, 10));

  useEffect(() => {
    if (quantity > maxQuantity) {
      setQuantity(Math.max(1, maxQuantity));
    }
  }, [maxQuantity, quantity]);

  function addToCart() {
    addItem(product, quantity, selectedVariant);
    const desc = selectedVariant
      ? `${quantity} × ${product.title} (${selectedVariant.color})`
      : `${quantity} × ${product.title}`;
    toast.success("Added to cart", { description: desc });
  }

  return (
    <div className={compact ? "" : "rounded border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900"}>
      {!compact && (
        <>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-950 dark:text-white">
              {formatPrice(currentPrice)}
            </span>
          </div>
          {currentDiscount > 0 && <p className="mt-0.5 text-sm text-amazon-red">({currentDiscount}% off)</p>}

          <p className={`mt-3 text-sm font-bold ${currentStock > 0 ? "text-amazon-green" : "text-amazon-red"}`}>
            {currentStock > 0 ? "In stock" : "Out of stock"}
          </p>
        </>
      )}

      {currentStock > 0 && (
        <div className="mt-3 flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Quantity:</span>
          <div className="inline-flex h-8 items-center rounded border border-slate-300 bg-slate-50 overflow-hidden shadow-xs dark:border-white/15 dark:bg-slate-800">
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
              className="flex h-full w-8 items-center justify-center text-slate-700 transition hover:bg-slate-200 hover:text-slate-950 active:bg-slate-300 disabled:pointer-events-none disabled:opacity-30 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
              aria-label="Decrease quantity"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="min-w-8 px-1 text-center text-sm font-bold text-slate-900 select-none dark:text-white">
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
              disabled={quantity >= maxQuantity}
              className="flex h-full w-8 items-center justify-center text-slate-700 transition hover:bg-slate-200 hover:text-slate-950 active:bg-slate-300 disabled:pointer-events-none disabled:opacity-30 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
              aria-label="Increase quantity"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 grid gap-2">
        <button
          type="button"
          onClick={addToCart}
          disabled={currentStock <= 0}
          className="flex h-10 items-center justify-center gap-2 rounded-full bg-amazon-gold text-sm font-bold text-slate-950 shadow-sm transition hover:bg-[#e07320] disabled:pointer-events-none disabled:opacity-50"
        >
          <ShoppingCart className="h-4 w-4" />
          Add to Cart
        </button>
        <Link
          href="/checkout"
          onClick={() => addItem(product, quantity, selectedVariant)}
          className="flex h-10 items-center justify-center gap-2 rounded-full bg-amazon-orange text-sm font-bold text-slate-950 shadow-sm transition hover:bg-[#e07320]"
        >
          <Zap className="h-4 w-4" />
          Buy Now
        </Link>
        <button
          type="button"
          onClick={() => {
            toggleWishlist(product);
            toast.success(isWishlisted ? "Removed from wishlist" : "Added to wishlist");
          }}
          className="flex h-10 items-center justify-center gap-2 rounded-full border border-slate-300 text-sm font-bold text-slate-800 transition hover:bg-slate-50 dark:border-white/20 dark:text-slate-100 dark:hover:bg-white/5"
        >
          <Heart className={`h-4 w-4 ${safeIsWishlisted ? "fill-red-500 text-red-500" : ""}`} />
          {safeIsWishlisted ? "In wishlist" : "Add to Wish List"}
        </button>
      </div>

      <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
        <Lock className="h-3.5 w-3.5" />
        Secure transaction
      </p>
    </div>
  );
}
